import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Paperclip, Send, X } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import PersonAvatar from '@/components/common/PersonAvatar';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';

export const THREAD_POLL_MS = 6000;
const MAX_FILES = 5;
const MAX_BYTES = 25 * 1024 * 1024;

type Attachment = {
  id: string;
  filename: string;
  file_size: number;
  mime_type: string;
  url: string;
};

type ThreadMessage = {
  id: string;
  side: 'customer' | 'platform' | 'system';
  sender_name: string;
  sender_email: string;
  sender_avatar_url: string;
  body: string;
  created_at: string;
  attachments: Attachment[];
};

type Thread = {
  report: {
    id: string;
    reporter_organization_name: string;
    reporter_avatar_url: string;
    your_side: 'customer' | 'platform';
  };
  messages: ThreadMessage[];
};

function sizeOf(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function Bubble({ message, mine }: { message: ThreadMessage; mine: boolean }) {
  return (
    <div className={`flex items-end gap-2 ${mine ? 'justify-end' : 'justify-start'}`}>
      {!mine && (
        <PersonAvatar
          name={message.sender_name || 'Customer'}
          photoUrl={message.sender_avatar_url}
          className="mb-4 h-7 w-7"
        />
      )}
      <div className="max-w-[80%] space-y-1">
        {!mine && message.sender_name && (
          <p className="px-1 text-[11px] text-muted-foreground">{message.sender_name}</p>
        )}
        <div
          className={`whitespace-pre-wrap break-words rounded-lg px-3 py-2 text-sm ${
            mine ? 'bg-primary text-primary-foreground' : 'bg-muted'
          }`}
          data-testid="report-message"
        >
          {message.body}
          {message.attachments.length > 0 && (
            <div className={`mt-2 space-y-1 ${message.body ? 'border-t pt-2' : ''}`}>
              {message.attachments.map((attachment) => (
                <a
                  key={attachment.id}
                  href={attachment.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1.5 text-xs underline"
                  data-testid={`report-attachment-${attachment.filename}`}
                >
                  <Paperclip className="h-3 w-3" />
                  {attachment.filename} ({sizeOf(attachment.file_size)})
                </a>
              ))}
            </div>
          )}
        </div>
        <p className={`px-1 text-[10px] text-muted-foreground ${mine ? 'text-right' : ''}`}>
          {new Date(message.created_at).toLocaleString()}
        </p>
      </div>
    </div>
  );
}

export default function ReportThread({ reportId }: { reportId: string }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = React.useState('');
  const [files, setFiles] = React.useState<File[]>([]);
  const fileInput = React.useRef<HTMLInputElement>(null);

  const thread = useQuery({
    queryKey: ['report-thread', reportId],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get(`/global-admin/advisor-reports/${reportId}/messages/`);
      return res.data as Thread;
    },
    refetchInterval: THREAD_POLL_MS,
    retry: false,
  });

  const messages = thread.data?.messages ?? [];

  const send = useMutation({
    mutationFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const form = new FormData();
      form.append('body', draft.trim());
      files.forEach((file) => form.append('attachments', file));
      const res = await api.post(
        `/global-admin/advisor-reports/${reportId}/messages/`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return res.data;
    },
    onSuccess: () => {
      setDraft('');
      setFiles([]);
      queryClient.invalidateQueries({ queryKey: ['report-thread', reportId] });
      queryClient.invalidateQueries({ queryKey: ['advisor-reports'] });
      queryClient.invalidateQueries({ queryKey: ['advisor-report-counts'] });
    },
    onError: () => toast.error('Could not send that.'),
  });

  const pickFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(event.target.files ?? []);
    event.target.value = '';
    const tooBig = picked.find((f) => f.size > MAX_BYTES);
    if (tooBig) {
      toast.error(`${tooBig.name} is larger than 25 MB.`);
      return;
    }
    setFiles([...files, ...picked].slice(0, MAX_FILES));
  };

  const canSend = (draft.trim().length > 0 || files.length > 0) && !send.isPending;

  return (
    <div className="rounded-md border" data-testid="report-thread">
      <div className="max-h-80 space-y-3 overflow-y-auto p-3">
        {thread.isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : messages.length === 0 ? (
          <p
            className="py-6 text-center text-xs text-muted-foreground"
            data-testid="report-thread-empty"
          >
            Nothing said yet. Ask them what you need to know — they see this in
            their own BitSwan, and the advisor does not.
          </p>
        ) : (
          messages.map((message) => (
            <Bubble
              key={message.id}
              message={message}
              mine={message.side === 'platform'}
            />
          ))
        )}
      </div>

      <div className="space-y-2 border-t p-3">
        {files.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="report-pending-attachments">
            {files.map((file, index) => (
              <Badge key={file.name + index} variant="secondary" className="gap-1">
                {file.name}
                <button
                  type="button"
                  onClick={() => setFiles(files.filter((_, i) => i !== index))}
                  aria-label={`Remove ${file.name}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          <Textarea
            rows={2}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Reply to the customer…"
            data-testid="report-reply-input"
          />
          <input
            ref={fileInput}
            type="file"
            multiple
            hidden
            onChange={pickFiles}
            data-testid="report-reply-file-input"
          />
          <Button
            variant="outline"
            size="icon"
            onClick={() => fileInput.current?.click()}
            aria-label="Attach a file"
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            disabled={!canSend}
            onClick={() => send.mutate()}
            aria-label="Send reply"
            data-testid="report-reply-send"
          >
            {send.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
